import { useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  HomeIcon, SparklesIcon, UsersIcon, BuildingStorefrontIcon, TruckIcon,
  UserGroupIcon, PaperAirplaneIcon, BuildingOffice2Icon, CubeIcon,
  ArchiveBoxIcon, MapPinIcon, CalendarDaysIcon, DocumentCheckIcon,
  BanknotesIcon, CurrencyDollarIcon, ArrowsRightLeftIcon, ChartBarIcon,
  ReceiptPercentIcon, CreditCardIcon, ArrowUturnLeftIcon, ClockIcon,
  TagIcon, GiftIcon, ArrowDownTrayIcon, ChartPieIcon, DocumentTextIcon,
  ChatBubbleLeftRightIcon, ClipboardDocumentListIcon, MagnifyingGlassIcon, XMarkIcon, ChevronLeftIcon, ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { AuthContext } from '../../contexts/AuthContext.jsx';

/**
 * Mismo formato que los proyectos hermanos (Turkimex/GGv2/PartnersV2, ver sus
 * `Sidebar.jsx`): lista plana con encabezados de sección (no acordeón), íconos por
 * ítem, fondo blanco, resaltado activo `bg-primary-50 text-primary-700`, y colapso a
 * un riel de solo-íconos (`isCollapsed`, ancho `w-20`) en vez del acordeón anterior.
 * Portado 2026-09-28 -- pedido explícito del usuario tras varias correcciones
 * ("el diseno debe ser igual que los demas sistemas", con captura real de Turkimex).
 */
const NAV = [
  { label: 'Inicio', path: '/', icon: HomeIcon, permiso: null },

  // Estructura y orden IGUALES al menú superior de V1 (Header.js): CATALOGOS, CONTENEDORES,
  // ADMIN, ASISTENTE IA, CXP, RETIROS, WHATSAPP, REPORTES, USUARIOS, LOGS. Las entradas de
  // cada sección siguen el orden de los `Botones*.jsx` de V1 (BotonesCatalogos, etc.).
  { label: 'Catálogos', tipo: 'section', permiso: 'catalogos:ver' },
  { label: 'Clientes', path: '/clientes', icon: UsersIcon, permiso: 'clientes:ver' },
  { label: 'Artículos', path: '/articulos', icon: CubeIcon, permiso: 'articulos:ver' },
  { label: 'Proveedores', path: '/proveedores', icon: BuildingStorefrontIcon, permiso: 'proveedores:ver' },
  { label: 'Navieras', path: '/proveedores?tipo=Naviera', icon: TruckIcon, permiso: 'navieras:ver' },
  { label: 'Agentes', path: '/proveedores?tipo=Agente', icon: UserGroupIcon, permiso: 'proveedores:ver' },
  { label: 'Forwarders', path: '/proveedores?tipo=Forwarder', icon: PaperAirplaneIcon, permiso: 'proveedores:ver' },
  { label: 'Empresas', path: '/empresas', icon: BuildingOffice2Icon, permiso: 'empresas:ver' },

  { label: 'Contenedores', tipo: 'section', permiso: 'contenedores:ver' },
  { label: 'Contenedores', path: '/contenedores', icon: ArchiveBoxIcon, permiso: 'contenedores:ver' },
  { label: 'Conceptos', path: '/conceptos-gastos', icon: TagIcon, permiso: 'conceptos-gastos:ver' },
  { label: 'Terminales', path: '/terminales', icon: MapPinIcon, permiso: 'contenedores:ver' },
  { label: 'Programas', path: '/programas', icon: CalendarDaysIcon, permiso: 'contenedores:ver' },
  { label: 'Patentes', path: '/patentes', icon: DocumentCheckIcon, permiso: 'contenedores:ver' },

  { label: 'Admin', tipo: 'section', permiso: null },
  { label: 'Contenedores', path: '/admin/contenedores', icon: ArchiveBoxIcon, permiso: 'contenedores:ver' },
  { label: 'Buscar Contenedor', path: '/admin/buscar-contenedor', icon: MagnifyingGlassIcon, permiso: 'contenedores:ver' },
  { label: 'Cargos', path: '/cargos', icon: ReceiptPercentIcon, permiso: 'cargos:ver' },
  { label: 'Cargos Contenedor', path: '/cargos-contenedor', icon: ReceiptPercentIcon, permiso: 'cargos:ver' },
  { label: 'Abonos', path: '/abonos', icon: CreditCardIcon, permiso: 'abonos:ver' },
  { label: 'Estado de Cuenta', path: '/reportes/estado-cuenta/clientes', icon: BanknotesIcon, permiso: 'estado-cuenta:clientes' },
  { label: 'Bancos', path: '/bancos', icon: CurrencyDollarIcon, permiso: 'bancos:ver' },
  { label: 'Traspasos entre Bancos', path: '/bancos-traspasos', icon: ArrowsRightLeftIcon, permiso: 'bancos-traspasos:ver' },
  { label: 'Anticipos', path: '/anticipos', icon: ClockIcon, permiso: 'anticipos:ver' },
  { label: 'Devoluciones', path: '/devoluciones', icon: ArrowUturnLeftIcon, permiso: 'devoluciones:ver' },
  { label: 'Movimientos Bancos', path: '/movimientos-bancos', icon: ChartBarIcon, permiso: 'movimientos-bancos:ver' },
  { label: 'Devoluciones Inversa', path: '/devoluciones-inversa', icon: ArrowUturnLeftIcon, permiso: 'devoluciones-inversa:ver' },

  // ASISTENTE IA: en V1 es un enlace directo del menú superior (sin submenú).
  { label: 'Asistente IA', path: '/asistente-ia', icon: SparklesIcon, permiso: 'asistente-ia:usar', aparte: true },

  { label: 'CXP', tipo: 'section', permiso: 'gastos:ver' },
  { label: 'Gastos', path: '/gastos', icon: ReceiptPercentIcon, permiso: 'gastos:ver' },
  { label: 'Gastos Contenedor', path: '/gastos-contenedor', icon: TruckIcon, permiso: 'gastos-contenedor:ver' },
  { label: 'Pagos', path: '/pagos', icon: CurrencyDollarIcon, permiso: 'pagos:ver' },
  { label: 'Anticipos', path: '/anticipos-proveedores', icon: ClockIcon, permiso: 'anticipos-proveedores:ver' },
  { label: 'Bonificaciones', path: '/bonificaciones-proveedores', icon: GiftIcon, permiso: 'bonificaciones-proveedores:ver' },
  { label: 'Estado de Cuenta', path: '/reportes/estado-cuenta/proveedores', icon: BanknotesIcon, permiso: 'estado-cuenta:proveedores' },

  { label: 'Retiros', tipo: 'section', permiso: 'socios:ver' },
  { label: 'Socios', path: '/socios', icon: UserGroupIcon, permiso: 'socios:ver' },
  { label: 'Por Retirar', path: '/por-retirar', icon: ClockIcon, permiso: 'por-retirar:ver' },
  { label: 'Retiros', path: '/retiros', icon: ArrowDownTrayIcon, permiso: 'retiros:ver' },

  // WHATSAPP: enlace directo en V1 (solo admin). Una sola pantalla: panel + "Telefonos Registrados".
  { label: 'WhatsApp', path: '/whatsapp', icon: ChatBubbleLeftRightIcon, permiso: 'ia:whatsapp', aparte: true },

  { label: 'Reportes', tipo: 'section', permiso: 'reportes:ver' },
  { label: 'Reporte Contenedores', path: '/reportes/contenedores', icon: ArchiveBoxIcon, permiso: 'contenedores:reporte' },
  { label: 'Reporte Transferencias', path: '/reportes/transferencias', icon: ArrowsRightLeftIcon, permiso: 'reportes:transferencias' },
  { label: 'Reporte Facturas', path: '/reportes/facturas', icon: DocumentTextIcon, permiso: 'reportes:facturas' },
  { label: 'Reporte Contenedores Con Cargos', path: '/reportes/contenedores-cargos', icon: ArchiveBoxIcon, permiso: 'contenedores:reporte' },
  { label: 'Reporte Utilidad', path: '/reportes/utilidad', icon: ChartBarIcon, permiso: 'contenedores:reporte' },
  { label: 'Gráficas', path: '/reportes/graficas', icon: ChartPieIcon, permiso: 'reportes:ver' },

  // USUARIOS: enlace directo en V1 (ListadoUsuarios).
  { label: 'Usuarios', path: '/usuarios', icon: UsersIcon, permiso: 'usuarios:ver', aparte: true },
  // LOGS: enlace directo en V1 (/MenuLogs, bitácora), último ítem del menú.
  { label: 'Logs', path: '/logs', icon: ClipboardDocumentListIcon, permiso: 'bitacora:ver', aparte: true },
];

/** `NavLink` compara solo pathname (ignora el querystring) -- comparación manual
 * para que Navieras/Agentes/Forwarders (mismo pathname, distinto `?tipo=`) no se
 * resalten los 3 a la vez (bug real corregido 2026-09-28). */
function esActivo(to, location) {
  const [path, query] = to.split('?');
  if (path !== location.pathname) return false;
  const tipoEsperado = new URLSearchParams(query || '').get('tipo') || '';
  const tipoActual = new URLSearchParams(location.search).get('tipo') || '';
  return tipoEsperado === tipoActual;
}

export default function Sidebar({ abierto, onCerrar, colapsado, onAlternarColapso }) {
  const { hasPermiso } = useContext(AuthContext);
  const location = useLocation();

  const permitidos = NAV.filter((item) => !item.permiso || hasPermiso(item.permiso));
  // Oculta encabezados de sección que se quedaron sin ninguna entrada visible debajo.
  const visibles = permitidos.filter((item, i) => {
    if (item.tipo !== 'section') return true;
    const sig = permitidos[i + 1];
    return sig && sig.tipo !== 'section' && !sig.aparte;
  });

  return (
    <>
      {abierto && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onCerrar} />}

      {/* Escritorio */}
      <aside className={`fixed inset-y-0 left-0 z-50 hidden lg:flex flex-col bg-white border-r border-slate-200 transition-all duration-300 ${colapsado ? 'w-20' : 'w-64'}`}>
        <SidebarContenido items={visibles} colapsado={colapsado} onAlternarColapso={onAlternarColapso} location={location} />
      </aside>

      {/* Móvil */}
      <aside className={`fixed inset-y-0 left-0 z-50 flex flex-col w-64 bg-white border-r border-slate-200 transition-transform duration-300 lg:hidden ${abierto ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <Logo />
          <button onClick={onCerrar} className="btn-ghost p-1.5 rounded-lg">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-0.5">
          {visibles.map((item, i) => <ItemNav key={i} item={item} colapsado={false} location={location} onClick={onCerrar} />)}
        </nav>
      </aside>
    </>
  );
}

function SidebarContenido({ items, colapsado, onAlternarColapso, location }) {
  return (
    <>
      <div className={`flex items-center border-b border-slate-200 h-16 px-4 ${colapsado ? 'justify-center' : 'justify-between'}`}>
        {!colapsado && <Logo />}
        <button onClick={onAlternarColapso} className="btn-ghost p-1.5 rounded-lg">
          {colapsado ? <ChevronRightIcon className="w-5 h-5 text-slate-500" /> : <ChevronLeftIcon className="w-5 h-5 text-slate-500" />}
        </button>
      </div>
      <nav className="flex-1 overflow-y-auto p-3 space-y-0.5">
        {items.map((item, i) => <ItemNav key={i} item={item} colapsado={colapsado} location={location} />)}
      </nav>
    </>
  );
}

function Logo() {
  return (
    <Link to="/" className="flex items-center gap-2">
      <img src="/LogoSistemify2RS.png" alt="logo" height="50" style={{ height: 40 }} />
    </Link>
  );
}

function ItemNav({ item, colapsado, location, onClick }) {
  if (item.tipo === 'section') {
    if (colapsado) return <div className="my-2 border-t border-slate-100" />;
    return <p className="px-3 pt-4 pb-1 text-xs font-semibold text-slate-400 uppercase tracking-wider">{item.label}</p>;
  }

  const Icon = item.icon;
  const activo = esActivo(item.path, location);
  return (
    <Link
      to={item.path}
      onClick={onClick}
      title={colapsado ? item.label : undefined}
      className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-150 ${item.aparte ? 'mt-3' : ''} ${
        activo ? 'bg-primary-50 text-primary-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
      } ${colapsado ? 'justify-center' : ''}`}
    >
      <Icon className="w-5 h-5 shrink-0" />
      {!colapsado && <span>{item.label}</span>}
    </Link>
  );
}
